import { useState } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { tokens } from "../../theme";
import { useCountUp, ValueWithLabel, TargetMedia, resolveCountLabel, } from "./targetShared";

const TargetMet = ({
  title = "",
  imgSrc,
  icon,
  mediaSize = 44,
  imgFit = "cover",
  imgRound = true,

  orientation = "horizontal",
  variant = "dash",
  fullWidth = false,

  bgColor,
  radius = 18,
  shadow = true,
  borderColor,
  titleColor,
  sx,

  defaultExpanded = false,
  onToggle,

  expandedDivider = true,
  expandedPaddingTop = 12,

  titleWrap = true,
  headerMinHeight,
  headerPaddingY,

  stats = [],
  columns = 2,

  detailMode = "both", // collapse, modal, both, none

  value,
  valueLabel = "",
  valueColor,
  duration = 900,

  allDetailsTitle = "Detalle",
  allDetailsTooltip = "Ver detalle (todo)",
  modalMaxWidth = 560,


  disableParticipantsDetail = false,
  participantsStatKey = "participantes",
  participantsLabelRegex = /particip/i,
}) => {
  const colors = tokens();
  const theme = useTheme();

  const fontFamily = theme.typography?.fontFamily || "inherit";
  const FW_BOLD = theme.typography?.fontWeightBold ?? 700;
  const FW_MED = theme.typography?.fontWeightMedium ?? 600;
  const FW_REG = theme.typography?.fontWeightRegular ?? 400;

  const [open, setOpen] = useState(defaultExpanded);
  const [activeKey, setActiveKey] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const hasCollapse = detailMode === "collapse" || detailMode === "both";
  const hasModal = detailMode === "modal" || detailMode === "both";

  const resolvedBg = bgColor ?? colors.primary[200];
  const resolvedBorder = borderColor ?? colors.grey[700];
  const resolvedTitle = titleColor ?? colors.primary[100];

  const hasMainValue = value !== undefined && value !== null;
  const { formatted } = useCountUp({
    value: Number(value) || 0,
    duration,
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      onToggle?.(next);
      if (!next) setActiveKey(null);
      return next;
    });
  };

  const statLabelOf = (stat) =>
    resolveCountLabel(stat?.label, stat?.value);

  const cardSx = {
    fontFamily,
    "&, & *": { fontFamily },
    "& .MuiTypography-root": { fontFamily },
    "& .MuiListItemText-primary, & .MuiListItemText-secondary": { fontFamily },

    backgroundColor: resolvedBg,
    border: `1px solid ${resolvedBorder}`,
    borderRadius: `${radius}px`,
    boxShadow: shadow ? `10px 10px 22px ${colors.grey[700]}` : "none",
    transition: "transform 140ms ease, box-shadow 140ms ease",
    cursor: "pointer",
    overflow: "hidden",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: shadow ? `10px 14px 26px ${colors.grey[700]}` : "none",
    },
    ...(fullWidth ? { width: "100%" } : {}),
    ...sx,
  };

  const titleSx = titleWrap
    ? {
      lineHeight: 1.15,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }
    : {
      lineHeight: 1.05,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

  const normalizeItems = (items, showFrequency = false) => {
    if (!items) return [];
    if (!Array.isArray(items)) return [String(items)];

    return items
      .map((it) => {
        if (it == null) return "";

        if (typeof it === "string" || typeof it === "number") {
          return String(it);
        }

        if (typeof it === "object") {
          const label = it.label ?? it.name ?? it.id ?? "";
          const value = it.value ?? it.count ?? it.total ?? it.frecuencia;

          if (showFrequency && value !== undefined && value !== null && value !== "") {
            const formattedValue =
              typeof value === "number" ? value.toLocaleString("es-CL") : value;

            return `${label} (${formattedValue})`;
          }

          if (label) return String(label);

          return JSON.stringify(it);
        }

        return String(it);
      })
      .filter(Boolean);
  };

  const statKeyOf = (s, idx) => s.key ?? `${statLabelOf(s)}-${idx}`;

  const isPeopleStat = (s, idx) => {
    if (!s) return false;

    const k = String(statKeyOf(s, idx) ?? "").toLowerCase().trim();
    const label = String(s?.label ?? "").toLowerCase().trim();

    const pk = String(participantsStatKey ?? "").toLowerCase().trim();
    if (pk && k === pk) return true;

    if (participantsLabelRegex && participantsLabelRegex.test(label)) return true;

    return (
      label.includes("particip") ||
      label.includes("formador") ||
      label.includes("persona")
    );
  };

  const isParticipantsStat = (s, idx) => {
    if (!disableParticipantsDetail) return false;
    return isPeopleStat(s, idx);
  };

  const shouldShowItemFrequency = (s, idx) => {
    if (!s) return false;
    if (typeof s?.showItemFrequency === "boolean") return s.showItemFrequency;
    return !isPeopleStat(s, idx);
  };



  const activeStatIndex = activeKey
    ? stats.findIndex((s, idx) => statKeyOf(s, idx) === activeKey)
    : -1;

  const activeStat = activeStatIndex >= 0 ? stats[activeStatIndex] : null;

  const handleStatClick = (s, idx, e) => {
    e.stopPropagation();


    if (isParticipantsStat(s, idx)) return;

    const key = statKeyOf(s, idx);

    if (detailMode === "modal") {
      setModalOpen(true);
      return;
    }
    if (!hasCollapse) return;

    setActiveKey((prev) => (prev === key ? null : key));
  };

  const AllDetailsButton = () => {
    if (!hasModal) return null;

    return (
      <Tooltip title={allDetailsTooltip}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setModalOpen(true);
          }}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            zIndex: 3,
            width: 36,
            height: 36,
            border: `1px solid ${resolvedBorder}`,
            backgroundColor: colors.grey[400],
            "&:hover": { backgroundColor: colors.grey[100] },
            color: colors.primary[100],
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const ExpandIcon = () => {
    if (!hasCollapse) return null;

    return (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        sx={{
          position: "absolute",
          right: 10,
          bottom: 10,
          zIndex: 2,
          width: 36,
          height: 36,
          border: `1px solid ${resolvedBorder}`,
          backgroundColor: colors.grey[400],
          "&:hover": { backgroundColor: colors.grey[100] },
          color: colors.primary[100],
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform .2s ease",
        }}
      >
        <ExpandMoreIcon />
      </IconButton>
    );
  };

  const StatBox = ({ s, idx }) => {
    const key = statKeyOf(s, idx);
    const isActive = key === activeKey;

    const isRestricted = isParticipantsStat(s, idx);
    const items = normalizeItems(s.items, shouldShowItemFrequency(s, idx));
    const hasItems = items.length > 0;

    const showHint = (hasCollapse || detailMode === "modal") && !isRestricted;

    return (
      <Box
        key={key}
        role={!isRestricted && (hasCollapse || detailMode === "modal") ? "button" : undefined}
        tabIndex={!isRestricted && (hasCollapse || detailMode === "modal") ? 0 : -1}
        onClick={(e) => handleStatClick(s, idx, e)}
        onKeyDown={(e) => {
          if (isRestricted) return;
          if (e.key === "Enter" || e.key === " ") handleStatClick(s, idx, e);
        }}
        sx={{
          backgroundColor: colors?.primary?.[300] ?? colors.grey[700],
          borderRadius: "10px",
          p: "10px 12px",
          border: `1px solid ${isActive
            ? (colors.green[200] ?? colors.grey[700])
            : (colors.primary[200] ?? colors.grey[700])
            }`,
          cursor: !isRestricted && (hasCollapse || detailMode === "modal") ? "pointer" : "default",
          outline: "none",
          transition: "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
          "&:hover": {
            transform: !isRestricted && (hasCollapse || detailMode === "modal") ? "translateY(-1px)" : "none",
            boxShadow:
              !isRestricted && (hasCollapse || detailMode === "modal")
                ? `0 10px 18px ${colors.grey[700]}`
                : "none",
          },
        }}
      >
        <Typography
          variant="caption"
          fontWeight={FW_BOLD}
          color={colors.primary[100] ?? "inherit"}
          sx={{ opacity: 0.9 }}
        >
          {statLabelOf(s)}
        </Typography>

        <Typography
          variant="h6"
          fontWeight={FW_BOLD}
          color={colors.green[200]}
          sx={{ lineHeight: 1.1 }}
        >
          {typeof s.value === "number" ? s.value.toLocaleString("es-CL") : s.value}
        </Typography>

        {showHint && (
          <Typography
            variant="caption"
            sx={{ opacity: 0.75, display: "block", mt: 0.4, fontWeight: FW_REG }}
          >
            {detailMode === "modal"
              ? "Ver detalle"
              : hasItems
                ? isActive
                  ? "Ocultar lista"
                  : "Ver lista"
                : "Sin datos"}
          </Typography>
        )}
      </Box>
    );
  };

  const BodyStats = () => (
    <Box>
      <Box display="grid" gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`} gap="10px">
        {stats.map((s, i) => (
          <StatBox key={statKeyOf(s, i)} s={s} idx={i} />
        ))}
      </Box>

      {hasCollapse && (
        <Collapse in={!!activeStat} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1.4 }}>
            <Divider sx={{ borderColor: colors.primary[300], my: 1 }} />

            <Typography variant="body2" fontWeight={FW_BOLD} color={colors.primary[100]} sx={{ mb: 0.8 }}>
              {activeStat ? statLabelOf(activeStat) : ""}
            </Typography>

            <Box
              sx={{
                border: `1px solid ${colors.primary[300]}`,
                borderRadius: "10px",
                backgroundColor: colors.grey[400],
                overflow: "hidden",
              }}
            >
              <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
                <List dense sx={{ py: 0 }}>
                  {normalizeItems(
                    activeStat?.items,
                    shouldShowItemFrequency(activeStat, activeStatIndex)
                  ).length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="Sin datos"
                        primaryTypographyProps={{
                          sx: { opacity: 0.8, fontWeight: FW_REG },
                        }}
                      />
                    </ListItem>
                  ) : (
                    normalizeItems(
                      activeStat?.items,
                      shouldShowItemFrequency(activeStat, activeStatIndex)
                    ).map((txt, idx) => (
                      <ListItem
                        key={`${activeKey}-item-${idx}`}
                        sx={{
                          py: 0.6,
                          borderBottom:
                            idx === normalizeItems(
                              activeStat?.items,
                              shouldShowItemFrequency(activeStat, activeStatIndex)
                            ).length - 1
                              ? "none"
                              : `1px solid ${colors.primary[300]}`,
                        }}
                      >
                        <ListItemText
                          primary={txt}
                          primaryTypographyProps={{
                            sx: { fontSize: 13, opacity: 0.92, fontWeight: FW_BOLD },
                          }}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );

  const DetailsModal = () => (
    <Dialog
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      PaperProps={{
        sx: {
          fontFamily,
          "&, & *": { fontFamily },
          "& .MuiTypography-root": { fontFamily },
          "& .MuiListItemText-primary, & .MuiListItemText-secondary": { fontFamily },

          borderRadius: 3,
          width: "92vw",
          maxWidth: `${modalMaxWidth}px`,
          overflow: "hidden",

          background: `linear-gradient(180deg, ${colors.primary[200]} 0%, ${colors.grey[100]} 100%)`,
          border: `1px solid ${colors.primary[300]}`,
          boxShadow: `0 18px 40px ${colors.grey[700]})`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.2,
          py: 1.6,
          borderBottom: `1px solid ${colors.primary[300]}`,
          backgroundColor: colors.primary[500],
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: FW_BOLD, fontSize: 18, lineHeight: 1.15, color: colors.primary[100] }}>
              {allDetailsTitle}
            </Typography>

            <Typography sx={{ mt: 0.35, fontSize: 12.5, opacity: 0.75, color: colors.primary[100] }}>
              Desglose por categoría
            </Typography>
          </Box>

          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{
              width: 36,
              height: 36,
              border: `1px solid ${colors.primary[300]}`,
              backgroundColor: colors.grey[400],
              "&:hover": { backgroundColor: colors.grey[100] },
              color: colors.primary[100],
              flex: "0 0 auto",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: 1.4,
          pb: 2,
          px: 2.2,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.6 }}>
          {stats.map((s, i) => {
            const key = statKeyOf(s, i);
            const items = normalizeItems(s.items, shouldShowItemFrequency(s, i));
            const itemsCount = items.length;

            const isRestricted = isParticipantsStat(s, i);

            return (
              <Box key={`modal-${key}`}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 0.7,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: FW_BOLD,
                      fontSize: 15.5,
                      color: colors.primary[100],
                    }}
                  >
                    {statLabelOf(s)}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12.5,
                      fontWeight: FW_MED,
                      opacity: 0.75,
                      color: colors.primary[100],
                    }}
                  >
                    {itemsCount}
                  </Typography>
                </Box>

                {isRestricted ? null : (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${colors.primary[300]}`,
                      backgroundColor: colors.grey[400],
                      overflow: "hidden",
                    }}
                  >
                    <Box sx={{ maxHeight: 160, overflowY: "auto" }}>
                      <List dense sx={{ py: 0.6 }}>
                        {items.length === 0 ? (
                          <ListItem sx={{ py: 0.6 }}>
                            <ListItemText
                              primary="Sin datos"
                              primaryTypographyProps={{
                                sx: {
                                  opacity: 0.75,
                                  fontStyle: "italic",
                                  fontSize: 13,
                                  fontWeight: FW_REG,
                                },
                              }}
                            />
                          </ListItem>
                        ) : (
                          items.map((txt, idx) => (
                            <ListItem
                              key={`modal-${key}-item-${idx}`}
                              sx={{
                                py: 0.5,
                                px: 1.4,
                                display: "flex",
                                alignItems: "center",
                                borderBottom:
                                  idx === items.length - 1
                                    ? "none"
                                    : `1px solid ${colors.primary[300]}`,
                              }}
                            >
                              <Box
                                sx={{
                                  mr: 1.2,
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  backgroundColor: colors.green[200],
                                  opacity: 0.9,
                                  flex: "0 0 auto",
                                }}
                              />
                              <ListItemText
                                primary={txt}
                                primaryTypographyProps={{
                                  sx: {
                                    fontSize: 13.2,
                                    lineHeight: 1.3,
                                    opacity: 0.92,
                                    fontWeight: FW_MED,
                                  },
                                }}
                              />
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );

  /* =============================
    VERTICAL / HERO
    ============================= */
  if (orientation === "vertical" || variant === "hero") {
    const size = Math.max(96, mediaSize);

    return (
      <>
        <Box sx={cardSx} onClick={toggle}>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pt: 3,
              px: 3,
              pb: 4.5,
              minHeight: headerMinHeight ?? 240,
              gap: 1.0,
            }}
          >
            <AllDetailsButton />
            <ExpandIcon />

            <TargetMedia
              imgSrc={imgSrc}
              icon={icon}
              size={size}
              title={title}
              imgFit={imgFit}
              imgRound={imgRound}
              border={resolvedBorder}
            />

            {hasMainValue && (
              <Box sx={{ textAlign: "center" }}>
                <ValueWithLabel
                  variant="h2"
                  formatted={formatted}
                  valueLabel={resolveCountLabel(valueLabel, value)}
                  fontFamily={fontFamily}
                  fontWeight={FW_BOLD}
                  color={valueColor ?? colors.green[200]}
                />
              </Box>
            )}

            {!!title && (
              <Typography
                variant="h5"
                fontWeight={FW_BOLD}
                color={resolvedTitle}
                sx={{
                  ...titleSx,
                  textAlign: "center",
                  minHeight: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title={title}
              >
                {title}
              </Typography>
            )}
          </Box>

          <Collapse in={open} timeout="auto" unmountOnExit>
            {expandedDivider && <Divider sx={{ borderColor: colors.primary[300] }} />}

            <Box
              sx={{ px: 2, pb: 2, pt: `${expandedPaddingTop}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              <BodyStats />
            </Box>
          </Collapse>
        </Box>

        {hasModal && <DetailsModal />}
      </>
    );
  }

  /* =============================
     HORIZONTAL / DASH
     ============================= */
  const py = headerPaddingY ?? 2.2;
  const minH = Math.max(headerMinHeight ?? 0, 95);

  return (
    <>
      <Box sx={cardSx} onClick={toggle}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1.6,
            px: 2,
            py,
            minHeight: minH,
            pr: 8,
          }}
        >
          <AllDetailsButton />
          <ExpandIcon />
          <TargetMedia
            imgSrc={imgSrc}
            icon={icon}
            size={mediaSize}
            title={title}
            imgFit={imgFit}
            imgRound={imgRound}
            border={resolvedBorder}
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {!!title && (
              <Typography
                variant="h6"
                fontWeight={FW_BOLD}
                color={resolvedTitle}
                sx={titleSx}
                title={title}
              >
                {title}
              </Typography>
            )}

            {hasMainValue && (
              <ValueWithLabel
                formatted={formatted}
                valueLabel={resolveCountLabel(valueLabel, value)}
                variant="h4"
                fontFamily={fontFamily}
                color={valueColor ?? colors.green[200]}
                sx={{ mt: 0.8 }}
              />
            )}
          </Box>
        </Box>

        <Collapse in={open} timeout="auto" unmountOnExit>
          {expandedDivider && <Divider sx={{ borderColor: colors.primary[300] }} />}
          <Box sx={{ px: 2, pb: 2, pt: `${expandedPaddingTop}px` }} onClick={(e) => e.stopPropagation()}>
            <BodyStats />
          </Box>
        </Collapse>
      </Box>

      {hasModal && <DetailsModal />}
    </>
  );
};

export default TargetMet;
